// Pre-scored slices of a soft roll, freed by a knife at a score. Each cut opens
// a gap; all slices spring to their new places with a little bounce, and the
// two slices beside a fresh cut lean away from it and settle. This follows
// graphics-atlas' bond-graph destruction (a broken bond splits a component that
// gets a kick), with springs instead of free flight. Design values only.
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export class CakeSlices {
  constructor(slices, { gap = .15, wedge = .05 } = {}) {
    // slices: [{min:[x,y,z], max:[x,y,z]}] ordered along +x in toy-local space.
    this.slices = slices.map(s => ({ min: [...s.min], max: [...s.max] }));
    this.count = this.slices.length; this.gap = gap; this.wedgeGap = wedge;
    this.cut = new Uint8Array(Math.max(0, this.count - 1));
    this.offset = new Float64Array(this.count); this.offsetVelocity = new Float64Array(this.count);
    this.tilt = new Float64Array(this.count); this.tiltVelocity = new Float64Array(this.count);
    this.targets = new Float64Array(this.count);
    this.wedgeSeam = -1; this.wedgeAmount = 0; this.cuts = 0; this.activity = 0;
  }
  // Toy-local x of score k between slice k and k+1, following the current offsets.
  seamX(k) { return (this.slices[k].max[0] + this.offset[k] + this.slices[k + 1].min[0] + this.offset[k + 1]) * .5; }
  seamNear(x, tolerance) {
    if (!Number.isFinite(x)) return -1;
    let best = -1, bestDistance = tolerance;
    for (let k = 0; k < this.cut.length; k++) { const d = Math.abs(x - this.seamX(k)); if (d <= bestDistance) { best = k; bestDistance = d; } }
    return best;
  }
  // A blade pressing into score k spreads its neighbours a little before the cut.
  setWedge(k, amount) { this.wedgeSeam = Number.isInteger(k) && k >= 0 && k < this.cut.length ? k : -1; this.wedgeAmount = this.wedgeSeam >= 0 && Number.isFinite(amount) ? clamp(amount, 0, 1) : 0; }
  slice(k) {
    if (!Number.isInteger(k) || k < 0 || k >= this.cut.length || this.cut[k]) return false;
    this.cut[k] = 1; this.cuts++;
    for (let i = 0; i < this.count; i++) this.offsetVelocity[i] += i <= k ? -1.1 : 1.1;
    this.tiltVelocity[k] += 1.8; this.tiltVelocity[k + 1] -= 1.8;
    return true;
  }
  // Clear every cut; the springs slide the slices back together.
  rejoin() { this.cut.fill(0); this.cuts = 0; this.setWedge(-1, 0); }
  reset() {
    this.rejoin(); this.offset.fill(0); this.offsetVelocity.fill(0); this.tilt.fill(0); this.tiltVelocity.fill(0); this.activity = 0;
  }
  updateTargets() {
    let total = 0, running = 0;
    for (let i = 0; i < this.count; i++) {
      if (i > 0 && this.cut[i - 1]) running += this.gap;
      this.targets[i] = running + (this.wedgeSeam >= 0 && i > this.wedgeSeam ? this.wedgeAmount * this.wedgeGap : 0);
      total += this.targets[i];
    }
    const mean = total / Math.max(1, this.count);
    for (let i = 0; i < this.count; i++) this.targets[i] -= mean;
  }
  step(dt) {
    if (!Number.isFinite(dt) || dt <= 0) return;
    dt = Math.min(dt, 1 / 30);
    this.updateTargets();
    const steps = Math.max(1, Math.ceil(dt / (1 / 120))), h = dt / steps;
    for (let s = 0; s < steps; s++) for (let i = 0; i < this.count; i++) {
      this.offsetVelocity[i] += ((this.targets[i] - this.offset[i]) * 120 - this.offsetVelocity[i] * 11) * h;
      this.offset[i] += this.offsetVelocity[i] * h;
      this.tiltVelocity[i] += (-this.tilt[i] * 60 - this.tiltVelocity[i] * 7) * h;
      this.tilt[i] += this.tiltVelocity[i] * h;
    }
    let activity = 0;
    for (let i = 0; i < this.count; i++) {
      if (Math.abs(this.offset[i] - this.targets[i]) < .0005 && Math.abs(this.offsetVelocity[i]) < .01) { this.offset[i] = this.targets[i]; this.offsetVelocity[i] = 0; }
      if (Math.abs(this.tilt[i]) < .0005 && Math.abs(this.tiltVelocity[i]) < .01) { this.tilt[i] = 0; this.tiltVelocity[i] = 0; }
      activity += Math.abs(this.offsetVelocity[i]) + Math.abs(this.tiltVelocity[i]) + Math.abs(this.offset[i] - this.targets[i]) + Math.abs(this.tilt[i]);
    }
    this.activity = activity < 1e-4 ? 0 : activity;
  }
  // Rigid pose of a slice vertex: lean about the bottom edge it tips toward, then slide along x.
  deform(i, out, normal) {
    if (i < 0 || i >= this.count) return;
    const slice = this.slices[i], tilt = this.tilt[i];
    if (tilt !== 0) {
      const c = Math.cos(tilt), s = Math.sin(tilt), px = tilt > 0 ? slice.min[0] : slice.max[0], py = slice.min[1];
      const x = out[0] - px, y = out[1] - py;
      out[0] = px + x * c - y * s; out[1] = py + x * s + y * c;
      if (normal) { const nx = normal[0], ny = normal[1]; normal[0] = nx * c - ny * s; normal[1] = nx * s + ny * c; }
    }
    out[0] += this.offset[i];
  }
}
